'use client';
import useWindowSize from '@repo/ui/src/hooks/useWindowSize';
import { buildImageSrc } from '@repo/ui/src/functions/image-functions-aspect-ratio';
import SocialLinks from '@repo/ui/src/components/SocialLinks';
import { imageFullWidthXs, imageFullWidthSmAndUp } from '@style/variables';
import BrandDivider from '@components/BrandDivider';
import { classNames } from '@repo/ui/src/utils/classNames';
import type { ImageProps, MediaType } from '@repo/ui/src/types';

export interface ProfileBannerProps {
  bannerImage?: ImageProps | MediaType;
  name?: string;
  title?: string;
  bannerContentAlignment?: 'left' | 'right' | 'center';
  smallBanner?: boolean;
  instagram?: string;
  twitter?: string;
  facebook?: string;
  youtube?: string;
  website?: string;
}

const ProfileBanner = ({
  bannerImage,
  name,
  title,
  bannerContentAlignment,
  smallBanner,
  instagram,
  twitter,
  facebook,
  youtube,
  website,
}: ProfileBannerProps) => {
  const { width = 0 } = useWindowSize();
  const xsOnly = width > 0 && width < 600;

  const imgUrl = buildImageSrc(
    bannerImage,
    xsOnly ? imageFullWidthXs : imageFullWidthSmAndUp,
    xsOnly ? 3 : 4,
    xsOnly ? 4 : 3,
  );

  const align = xsOnly ? 'left' : bannerContentAlignment || 'center';

  return (
    <div
      style={{
        background: `url('${imgUrl}') center center / cover no-repeat`,
      }}
      className={classNames(
        'relative max-h-[800px] overflow-hidden',
        smallBanner ? 'h-auto' : 'h-[550px] sm:h-[100vh]',
      )}
    >
      {!smallBanner && (
        <div
          className={classNames(
            'h-full w-full absolute z-0 from-transparent via-50% via-[rgba(0,0,0,0.6)] to-[rgba(0,0,0,0.6)]',
            bannerContentAlignment === 'right' && 'bg-gradient-to-r',
            bannerContentAlignment === 'left' && 'bg-gradient-to-r',
            bannerContentAlignment === 'center' && 'bg-[rgba(0,0,0,0.6)]',
          )}
        />
      )}
      <div className="relative h-full flex flex-col justify-center items-center">
        <div
          className={classNames(
            'flex flex-col w-full px-4 sm:px-8 md:px-12 py-8 sm:py-12',
            align === 'left' && 'items-start',
            align === 'right' && 'items-end',
            align === 'center' && 'items-center',
          )}
        >
          <div
            className={classNames(
              'w-full sm:w-1/2 md:w-5/12 lg:w-1/3 xl:w-1/4',
              align === 'center' && 'text-center',
            )}
          >
            {name && <h1 className="tra-text-web-heading-2 text-neutral-50">{name}</h1>}
            <BrandDivider align={align === 'center' ? 'center' : 'left'} />
            {title && <p className="tra-text-web-body-l mt-6 mb-8 text-neutral-50">{title}</p>}
            <div
              className={classNames(
                'animate-in flex',
                align === 'center' ? 'justify-center' : 'justify-items-start',
              )}
            >
              <SocialLinks
                facebook={facebook}
                twitter={twitter}
                youtube={youtube}
                instagram={instagram}
                website={website}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileBanner;
